import { useEffect, useState } from 'react';

import { resolveCanvasWidth } from '../utils/pageCanvas';
import { pageRightInset } from '../utils/pageWidth';

export type PageCanvas = { width: number; height: number };

/**
 * The page canvas, measured from the DOM and shared by every subscriber.
 *
 * It reads the inset custom property the drawer already writes rather than observing the shell
 * element, which is why nothing under `src/app-assistant/` has to know this exists. The property
 * jumps straight to its final value — the 250ms slide is the shell's `padding-right` transition
 * catching up — so subscribers get the settled canvas on the first frame instead of fifteen
 * intermediate ones.
 */

let snapshot: PageCanvas | null = null;
let measured = false;
let frame: number | null = null;
const listeners = new Set<() => void>();

function measure(): PageCanvas | null {
  if (typeof document === 'undefined') return null;
  const width = resolveCanvasWidth(document.documentElement.clientWidth, window.innerWidth, pageRightInset());
  if (width === null) return null;
  return { width, height: window.innerHeight };
}

function publish() {
  frame = null;
  // While the edge is being dragged the inset changes every frame; hold the last settled value
  // and let the attribute's removal on release schedule the flush.
  if (document.documentElement.dataset.appAssistantResizing) return;

  const next = measure();
  if (next?.width === snapshot?.width && next?.height === snapshot?.height) return;
  snapshot = next;
  measured = true;
  listeners.forEach((listener) => listener());
}

function schedule() {
  if (frame !== null) return;
  frame = requestAnimationFrame(publish);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    window.addEventListener('resize', schedule);
    // The drawer publishes both the inset and the resizing flag on <html>, so one observer
    // covers open, close, drag and release.
    observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, { attributeFilter: ['style', 'data-app-assistant-resizing'] });
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    window.removeEventListener('resize', schedule);
    observer?.disconnect();
    observer = null;
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    // The next mount must re-measure: the window may have moved while nothing was listening.
    measured = false;
  };
}

let observer: MutationObserver | null = null;

function getSnapshot(): PageCanvas | null {
  // Hands back the SAME object until a real change, so subscribers re-render on width, not
  // on every mutation of <html>'s style attribute (HeaderProvider writes there too).
  if (!measured) {
    snapshot = measure();
    measured = true;
  }
  return snapshot;
}

/** Null when no usable width could be measured — callers must not substitute a guess. */
export function usePageCanvas(): PageCanvas | null {
  const [canvas, setCanvas] = useState<PageCanvas | null>(getSnapshot);

  useEffect(() => {
    const sync = () => setCanvas(getSnapshot());
    const unsubscribe = subscribe(sync);
    // Adopt whatever the shared snapshot holds now — another subscriber may have
    // republished between this component's lazy initializer and this effect.
    sync();
    // …and re-MEASURE, because sync() alone only re-reads the cache: a resize that landed
    // while nothing was subscribed left the snapshot itself stale.
    schedule();
    return unsubscribe;
  }, []);

  return canvas;
}
